/**
 * One clip's discussion: every page of it, and the three writes the trainer makes in it.
 *
 * Every write is optimistic and rolls back its own change alone on failure (`model/threadCache`).
 * A write never refetches the timeline: that read mints a fresh signed url and a new `src` would
 * restart the film under the trainer. The timeline's own copy of the thread (the Forma dock's last
 * comment) is patched in place instead, and the thread itself is refetched once the write settles.
 */

import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  clipCommentsQueryKey,
  clipTimelineQueryKey,
  toClipComment,
  type ClipComment,
  type ClipEntry,
  type ClipPerson,
} from '~/entities/exercise-clip';
import {
  deleteClipsClipIdCommentsCommentId,
  getClipsClipIdComments,
  patchClipsClipIdCommentsCommentId,
  postClipsClipIdComments,
} from '~/shared/api/generated/endpoints/clip/clip';
import { isGone } from '~/shared/api/mutator';

import {
  appendComment,
  confirmComment,
  removeComment,
  replaceComment,
  restoreComment,
  withEarlyComments,
  type ThreadPages,
} from '../model/threadCache';

/** The backend's own page ceiling. A thread holds at most 200, so this is two reads at worst. */
const PAGE = 100;

interface CommentPage {
  readonly entries: readonly ClipComment[];
  readonly nextCursor: number | null;
}

type Pages = InfiniteData<CommentPage, number | null>;

export interface ClipDiscussion {
  /** Oldest first. */
  readonly comments: readonly ClipComment[];
  readonly pending: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
  /** Each write names the clip it is for; a clip that is not the one open refuses it. */
  readonly post: (clipId: number, body: string, offsetMs: number | null) => Promise<boolean>;
  readonly edit: (
    clipId: number,
    commentId: number,
    body: string,
    offsetMs: number | null,
  ) => Promise<boolean>;
  readonly remove: (clipId: number, commentId: number) => Promise<boolean>;
}

interface EarlyComment {
  readonly clipId: number;
  readonly comment: ClipComment;
}

let temporaryIds = 0;

/**
 * @param owned whether the clip is known to be this client's. The thread is read from the start,
 *   but shows nothing and takes no write until it is: a foreign clip's thread answers 404 anyway.
 * @param onSettled called once a write has been answered either way — the attention queue and the
 *   movement list read what the trainer answered, and are refreshed from it.
 */
export function useClipDiscussion(
  clipId: number,
  owned: boolean,
  timeline: { readonly clientId: number; readonly exerciseId: number } | null,
  viewer: ClipPerson,
  onSettled: () => void,
): ClipDiscussion {
  const queryClient = useQueryClient();
  const key = clipCommentsQueryKey(clipId);
  const shown = owned ? clipId : null;
  // Comments sent before the thread was read (`withEarlyComments`).
  const [early, setEarly] = useState<readonly EarlyComment[]>([]);

  const pages = useInfiniteQuery({
    queryKey: key,
    queryFn: async ({ pageParam, signal }): Promise<CommentPage> => {
      const page = await getClipsClipIdComments(
        clipId,
        pageParam === null ? { limit: PAGE } : { limit: PAGE, before: pageParam },
        undefined,
        signal,
      );
      return {
        entries: (page.entries ?? []).flatMap((entry) => {
          const comment = toClipComment(entry);
          return comment === null ? [] : [comment];
        }),
        nextCursor: page.nextCursor ?? null,
      };
    },
    initialPageParam: null as number | null,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: (count, error) => !isGone(error) && count < 1,
  });
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = pages;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const thread =
    pages.data === undefined || shown === null
      ? null
      : [...pages.data.pages].reverse().flatMap((page) => page.entries);
  const comments = withEarlyComments(
    thread,
    early.filter((entry) => entry.clipId === shown).map((entry) => entry.comment),
  );

  /**
   * One change, to the thread's pages and to the timeline's copy of the newest page alike. A thread
   * that has not been read is left unread: writing into it would stand in for the answer.
   */
  const apply = (target: number, change: (pages: ThreadPages) => ThreadPages) => {
    queryClient.setQueryData<Pages>(clipCommentsQueryKey(target), (current) => {
      if (current === undefined) return undefined;
      const next = change(current.pages.map((page) => page.entries));
      return {
        pages: next.map((entries, index) => ({
          entries,
          nextCursor: current.pages[index]?.nextCursor ?? null,
        })),
        pageParams: current.pageParams,
      };
    });
    if (timeline === null) return;
    queryClient.setQueryData<readonly ClipEntry[]>(
      clipTimelineQueryKey(timeline.clientId, timeline.exerciseId),
      (clips) =>
        clips?.map((clip) =>
          clip.id === target ? { ...clip, comments: change([clip.comments])[0] ?? [] } : clip,
        ),
    );
  };
  const changeEarly = (change: (comment: ClipComment) => ClipComment | null) => {
    setEarly((list) =>
      list.flatMap((entry) => {
        const comment = change(entry.comment);
        return comment === null ? [] : [{ ...entry, comment }];
      }),
    );
  };

  /** Before an optimistic write: a refetch already in flight would land over it and erase it. */
  const settleReads = (target: number) =>
    queryClient.cancelQueries({ queryKey: clipCommentsQueryKey(target) });
  const threadRead = (target: number) =>
    queryClient.getQueryData<Pages>(clipCommentsQueryKey(target)) !== undefined;

  /** One write to a clip's thread; answered either way, the thread is re-read and the host told. */
  const write = async <T>(target: number, request: () => Promise<T>): Promise<T> => {
    try {
      return await request();
    } finally {
      void queryClient.invalidateQueries({ queryKey: clipCommentsQueryKey(target) });
      onSettled();
    }
  };

  return {
    comments,
    pending:
      shown === null ||
      pages.isPending ||
      (pages.isError && pages.isFetching) ||
      (hasNextPage && !isError),
    failed: shown !== null && pages.isError,
    retry: () => {
      void pages.refetch();
    },
    post: async (target, body, offsetMs) => {
      if (target !== shown) return false;
      temporaryIds -= 1;
      const temporary: ClipComment = {
        id: temporaryIds,
        author: viewer,
        body,
        createdAt: Date.now(),
        editedAt: null,
        offsetMs,
      };
      if (threadRead(target)) {
        await settleReads(target);
        apply(target, (current) => appendComment(current, temporary));
      } else {
        // The first read keeps running; the comment waits beside it.
        setEarly((list) => [...list, { clipId: target, comment: temporary }]);
      }
      try {
        const confirmed = toClipComment(
          await write(target, () => postClipsClipIdComments(target, { body, offsetMs })),
        );
        apply(target, (current) =>
          confirmed === null
            ? removeComment(current, temporary.id)
            : confirmComment(current, temporary.id, confirmed),
        );
        changeEarly((comment) => (comment.id === temporary.id ? confirmed : comment));
        return true;
      } catch {
        apply(target, (current) => removeComment(current, temporary.id));
        changeEarly((comment) => (comment.id === temporary.id ? null : comment));
        return false;
      }
    },
    edit: async (target, commentId, body, offsetMs) => {
      if (target !== shown) return false;
      await settleReads(target);
      const before = comments.find((entry) => entry.id === commentId);
      const editedAt = Date.now();
      apply(target, (current) =>
        replaceComment(current, commentId, (entry) => ({ ...entry, body, editedAt })),
      );
      try {
        await write(target, () =>
          patchClipsClipIdCommentsCommentId(target, commentId, { body, offsetMs }),
        );
        changeEarly((comment) => (comment.id === commentId ? { ...comment, body, editedAt } : comment));
        return true;
      } catch {
        if (before !== undefined) {
          // Only this edit is undone, and only while the comment still carries it.
          apply(target, (current) =>
            replaceComment(current, commentId, (entry) =>
              entry.editedAt === editedAt ? before : entry,
            ),
          );
        }
        return false;
      }
    },
    remove: async (target, commentId) => {
      if (target !== shown) return false;
      await settleReads(target);
      const removed = comments.find((entry) => entry.id === commentId);
      apply(target, (pagesNow) => removeComment(pagesNow, commentId));
      try {
        await write(target, () => deleteClipsClipIdCommentsCommentId(target, commentId));
        changeEarly((comment) => (comment.id === commentId ? null : comment));
        return true;
      } catch {
        if (removed !== undefined) apply(target, (pagesNow) => restoreComment(pagesNow, removed));
        return false;
      }
    },
  };
}
